import React, { useEffect, useRef, useState } from "react";
import { Loader2, AlertCircle, Anchor } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  consumeMicrosoftRedirect,
  describeSsoBackendError,
  FederatedAuthError,
} from "@/lib/federatedAuth";

// Microsoft Entra redirect return leg (open registration · 2026-02-20).
// Mounted by <AuthGate> when the URL contains an MSAL response
// fragment or a pending redemption is stashed in sessionStorage
// (see `hasPendingMicrosoftRedirect`). Completes the handshake
// against the single open-registration signin endpoint:
//
//   POST /api/auth/sso/microsoft/signin
//     · handles both first-time signup and returning sign-in
//     · see backend/routes_sso_signin.py
export default function MicrosoftAuthCallback() {
  const hasProcessed = useRef(false);
  const { refreshMe } = useAuth();
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    (async () => {
      let result;
      try {
        result = await consumeMicrosoftRedirect();
      } catch (err) {
        _clearHash();
        setError(err instanceof FederatedAuthError
          ? err.message
          : formatApiError(err));
        return;
      }

      if (!result) {
        _clearHash();
        try { await refreshMe(); } catch { /* ignore */ }
        return;
      }

      // Native sign-in: the backend already exchanged the PKCE code and the
      // bearer pair is stored — nothing to post; just load the account.
      if (result.native && result.intent !== "link") {
        _clearHash();
        try {
          await refreshMe();
        } catch (err) {
          setError(formatApiError(err));
        }
        return;
      }

      try {
        const isLink = result.intent === "link";
        await api.post(
          isLink ? `/api/auth/sso/microsoft/link` : `/api/auth/sso/microsoft/signin`,
          {
            provider_token: result.id_token,
            nonce: result.nonce,
          },
        );
        _clearHash();
        if (isLink) {
          try { sessionStorage.setItem("cc_link_result", "microsoft:linked"); } catch { /* ignore */ }
          window.history.replaceState({}, "", window.location.pathname + window.location.search + "#settings");
        }
        await refreshMe();
      } catch (err) {
        _clearHash();
        const described = describeSsoBackendError(err);
        if (result.intent === "link") {
          try { sessionStorage.setItem("cc_link_result", `microsoft:error:${described?.message || ""}`); } catch { /* ignore */ }
          window.history.replaceState({}, "", window.location.pathname + window.location.search + "#settings");
          try { await refreshMe(); } catch { /* ignore */ }
          return;
        }
        setError(described?.message || formatApiError(err));
      }
    })();
  }, [refreshMe]);

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center bg-[#0F172A] text-white"
      data-testid="cc-microsoft-auth-callback"
    >
      <div className="max-w-sm text-center px-8">
        <div className="w-11 h-11 mx-auto mb-6 rounded-xl bg-white/10 border border-white/20 backdrop-blur flex items-center justify-center">
          <Anchor className="w-5 h-5 text-white" strokeWidth={2.2} />
        </div>
        {!error ? (
          <>
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-4 text-white/80" />
            <p
              className="text-[13.5px] tracking-[0.16em] uppercase font-semibold text-white/70"
              data-testid="cc-microsoft-auth-callback-status"
            >
              Completing Microsoft sign-in…
            </p>
          </>
        ) : (
          <>
            <AlertCircle className="w-6 h-6 mx-auto mb-4 text-red-400" />
            <p
              className="text-[14px] text-white/80 leading-relaxed mb-6"
              data-testid="cc-microsoft-auth-callback-error"
            >
              {error}
            </p>
            <a
              href="/"
              className="inline-flex items-center gap-2 rounded-full px-5 h-11 text-[13px] font-bold text-[#1a1204]"
              style={{ background: "linear-gradient(135deg, #ffd66b 0%, #f5b400 100%)" }}
              data-testid="cc-microsoft-auth-callback-back"
            >
              Back to sign in
            </a>
          </>
        )}
      </div>
    </div>
  );
}

function _clearHash() {
  try {
    const clean = window.location.pathname + window.location.search;
    window.history.replaceState({}, "", clean);
  } catch { /* ignore */ }
}
